import { useContext, createContext } from 'react'
import {
  useMe,
  OpalApiResponseDocument,
  findResourceOfType,
} from '@opallabs/api'
import { User } from '@opallabs/concepts'

const CurrentUserContext = createContext<
  { user: User | undefined } | undefined
>(undefined)

const selectUser = (
  response: OpalApiResponseDocument<'ReadUserV2', 200>
): User => {
  const assetReference = response.included?.find(
    findResourceOfType(
      'asset_reference',
      'V2',
      response.data.relationships.avatar_asset_reference?.data?.id
    )
  )
  return {
    uuid: response.data.attributes.uuid,
    name: response.data.attributes.full_name,
    asset: assetReference && {
      id: assetReference.attributes.uuid,
      url: assetReference.attributes.full_url ?? '',
      title: 'User Avatar',
    },
    legacyId: response.data.id,
    type: 'user',
  }
}

/*
 * A provider to fetch the current user.
 */
const CurrentUserProvider = ({ children }: { children?: React.ReactNode }) => {
  const currentUserQuery = useMe({
    select: selectUser,
  })

  return (
    <CurrentUserContext.Provider value={{ user: currentUserQuery.data }}>
      {children}
    </CurrentUserContext.Provider>
  )
}

/*
 * Returns the current logged in user.
 */
const useCurrentUser = () => {
  const context = useContext(CurrentUserContext)

  if (context === undefined) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider')
  }
  return context.user
}

export { CurrentUserProvider, useCurrentUser }
